import { ComponentProps, MouseEvent, forwardRef } from 'react';

import { RiCloseLine as CloseIcon } from '@remixicon/react';

import { ButtonIcon } from '@spotify-internal/encore-web';

import { asDialogSlot } from './composition';
import { useDialogContext } from './context';

export type CloseButtonProps = Omit<
  ComponentProps<typeof ButtonIcon>,
  'aria-label' | 'children' | 'variant'
> & {
  variant?: ComponentProps<typeof ButtonIcon>['variant'];
};

/**
 * @deprecated The Encore dialog molecules own their own dismissal (footer
 * actions, the backdrop, Escape, and the full-screen molecule's built-in close
 * button), so a `CloseButton` placed in the `Header` renders nothing and its
 * `onClick` never fires. Remove it when migrating a dialog to the Encore
 * `DialogComposite`. (The MUI `DialogCompositeMui.CloseButton` still renders it
 * and is not deprecated.)
 */
export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(
  (props, ref) => {
    const { className, onClick, variant = 'tertiary', ...rest } = props;
    const { onClose } = useDialogContext();
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (e.defaultPrevented) {
        return;
      }
      onClose();
    };

    return (
      <ButtonIcon
        aria-label="close"
        variant={variant}
        size="small"
        onClick={handleClick}
        ref={ref}
        className={className}
        data-track-id="button.close"
        data-track-action="dismiss"
        {...rest}
      >
        <CloseIcon />
      </ButtonIcon>
    );
  },
);

CloseButton.displayName = 'DialogComposite.CloseButton';

asDialogSlot(CloseButton, 'closeButton');

export default CloseButton;
